import React, { useState, useEffect, useRef, forwardRef, useCallback, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useParams } from "react-router-dom";

import { makeStyles } from "tss-react/mui";

import DialogActions from "@mui/material/DialogActions";
import Box from "@mui/material/Box";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import ListItemButton from "@mui/material/ListItemButton";
import Checkbox from "@mui/material/Checkbox";
import TableSortLabel from "@mui/material/TableSortLabel";
import TablePagination from "@mui/material/TablePagination";
import Tooltip from "@mui/material/Tooltip";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Slide from "@mui/material/Slide";
import ListItemText from "@mui/material/ListItemText";
import Chip from "@mui/material/Chip";

import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";

import { visuallyHidden } from "@mui/utils";

import { ButtonUI, ConfirmPopup, KMKDialog } from "../../Common";

import { showToast, syncProcessQueue } from "../../../store/actions";
import { regexFileName } from "../../../util/regularExpress";
import ProcessQueueService from "../../../service/ProcessQueueService";
import fileUploader from "../../../util/fileUploader";
import { updateProcessingStatus } from "../../../store/actions/processQueue";
import CopyButton from "../../Common/CopyButton";
import { convertTimezone, setPrevState } from "../../../util/common";
import useWindowResize from "../../../hooks/useWindowsReszie";
import SelectedFilesDialog from "./SelectedFilesDialog";

const Transition = forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const useStyles = makeStyles()((theme) => ({
  dialogPaper: {
    "& .MuiFormHelperText-root": {
      marginLeft: 0,
    },
    "& .MuiGrid-item .MuiTextField-root": {
      marginTop: 0,
    },
    "& .MuiFormControl-root": {
      margin: 0,
    },
    "& .MuiSvgIcon-root": {
      verticalAlign: "middle",
    },
  },
  container: {
    padding: "8px 24px 0 24px",
    backgroundColor: "#FFFFFF",
  },
  bodyContainer: {
    width: "100%",
    marginLeft: "0",
    marginBottom: "0",
  },
  Toolbar: {
    fontSize: "1.1rem",
    backgroundColor: "white",
    color: "black",
  },
  LogoImg: {
    width: "20px",
    marginRight: "10px",
  },
  searchContainer: {
    minWidth: "250px",
    marginRight: "12px",
  },
  headerIcons: {
    marginRight: "10px",
  },
  createFolder: {
    display: "flex",
    alignItems: "center",
    marginBottom: "20px",
    width: "100%",
    "& button": {
      height: "33px",
    },
  },
  innerDialog: {
    "& .MuiPaper-root": {
      width: "100%",
    },
  },
  listItem: {
    display: "inline-flex",
    color: "#1976d2",
    fontSize: "1rem",
    margin: "0 1px",
    "& .MuiTypography-root": {
      fontSize: "0.875rem",
    },
    "&.MuiListItemButton-root": {
      padding: "0px !important",
    },
    "&.MuiListItemButton-root:hover": {
      textDecoration: "underline",
      color: "#ed6c02",
      backgroundColor: "transparent",
    },
  },
  BucketContainer: {
    overflowY: "hidden",
    overflowX: "auto",

    marginBottom: "2px",
    minWidth: "min-content",
    whiteSpace: "nowrap",
  },
  TableHead: {
    "& th": {
      position: "sticky",
      top: "0px",
      zIndex: 2,
      backgroundColor: "rgb(255, 255, 255)",
    },
  },
  bucketNameField: {
    display: "inline",
    margin: "0",
    padding: "0",
    textAlign: "left",
    maxWidth: "230px",
    wordBreak: "break-word",
  },
  selectableRow: {
    cursor: "pointer",
    "&:hover": {
      background: "rgba(0, 0, 0, 0.1)",
    },
  },
  selectedRow: {
    background: "rgba(0, 0, 0, 0.1)",
    "&:hover": {
      background: "rgba(0, 0, 0, 0.1)",
    },
  },
  TablePagination: {
    width: "100%",
    position: "relative",
    display: "flex",
    alignItems: "center",
    "& .MuiToolbar-root": {
      width: "100%",
      "& p": {
        margin: 0,
      },
    },
  },
  FirstTableCell: {
    wordBreak: "break-all",
    display: "inline-block",
    width: "210px !important",
    verticalAlign: "middle",
    [theme.breakpoints.down("md")]: {
      width: "auto",
    },
  },
  countClass: {
    marginLeft: "8px",
  },
}));

const CONNECTION_TYPES = {
  s3: 1,
  azure: 2,
  salesforce: 3,
  sftp: 4,
  sharepoint: 5,
};

//Headcells
const headCellList = [
  {
    id: "name",
    numeric: false,
    disablePadding: false,
    label: "NAME",
    sortable: true,
    align: "left",
    width: "40%",
  },
  {
    id: "fileExtension",
    numeric: false,
    disablePadding: false,
    label: "TYPE",
    sortable: true,
    align: "center",
    width: "15%",
  },
  {
    id: "size",
    numeric: true,
    disablePadding: false,
    label: "SIZE",
    sortable: true,
    align: "center",
    width: "15%",
  },
  {
    id: "lastModified",
    numeric: true,
    disablePadding: false,
    label: "LAST MODIFIED",
    sortable: true,
    align: "center",
    width: "15%",
  },
  {
    id: "action",
    numeric: true,
    disablePadding: false,
    label: "ACTION",
    sortable: false,
    align: "right",
    width: "15%",
  },
];

//Rable Head with sorting
function EnhancedTableHead(props) {
  const { order, orderBy, onRequestSort, tableHeadRef, selectedFileCount, handleSelectAllFiles, isAnyFileUnselected } =
    props;
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead ref={tableHeadRef}>
      <TableRow>
        {headCellList.map((headCell, i) => (
          <TableCell
            sx={{ fontWeight: 600, ...(headCell.width ? { width: headCell.width } : {}) }}
            key={headCell.id}
            align={headCell.align}
            padding={headCell.disablePadding ? "none" : "normal"}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            {headCell.id === "name" ? (
              <Tooltip title="Select all files" placement="top" arrow>
                <Checkbox
                  sx={{ justifyContent: "center", alignItems: "center", mr: 1.5 }}
                  margin="auto"
                  color="primary"
                  indeterminate={selectedFileCount !== 0 && isAnyFileUnselected}
                  checked={selectedFileCount !== 0 && !isAnyFileUnselected}
                  onChange={handleSelectAllFiles}
                />
              </Tooltip>
            ) : (
              <></>
            )}
            {headCell.sortable ? (
              <TableSortLabel
                active={orderBy === headCell.id}
                direction={orderBy === headCell.id ? order : "asc"}
                onClick={createSortHandler(headCell.id)}
              >
                {headCell.label}
                {orderBy === headCell.id ? (
                  <Box component="span" sx={visuallyHidden}>
                    {order === "desc" ? "sorted descending" : "sorted ascending"}
                  </Box>
                ) : null}
              </TableSortLabel>
            ) : (
              headCell.label
            )}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

const PopupCreateFolder = ({
  folder,
  setFolder,
  handleFolderName,
  handleClose,
  folderPath,
  handleManageObjectEvent,
  loading,
}) => {
  const { classes } = useStyles();

  useEffect(() => {
    const handleKeys = (event) => {
      if (event.key === "Enter" && folder) handleManageObjectEvent();
    };

    document.addEventListener("keydown", handleKeys, false);
    return () => document.removeEventListener("keydown", handleKeys, false);
  }, [folder, handleManageObjectEvent]);

  return (
    <KMKDialog maxWidth="sm" open={true} className={classes.innerDialog} onClose={handleClose}>
      <Backdrop sx={{ backgroundColor: "transparent", zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
        <CircularProgress color="primary" />
      </Backdrop>
      <AppBar sx={{ position: "relative" }}>
        <Toolbar className={classes.Toolbar}>Create Folder</Toolbar>
        <IconButton
          onClick={handleClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: "black",
          }}
        >
          <CloseIcon />
        </IconButton>
      </AppBar>

      <Box sx={{ width: "100%" }} className={classes.container}>
        <Typography sx={{ marginBottom: 0, maxWidth: "100%", wordBreak: "break-all" }}>
          Create folder inside {folderPath}
        </Typography>
        <TextField
          margin="dense"
          required
          inputProps={{ maxLength: 255 }}
          fullWidth
          value={folder}
          onChange={(e) => {
            if (e.target.value === "") setFolder("");
            else handleFolderName(e.target.value);
          }}
          label="Name"
          variant="standard"
          sx={{ mr: "20px", flex: 1 }}
          autoFocus
        />
      </Box>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <ButtonUI variant="contained" color="secondary" onClick={handleClose}>
          Cancel
        </ButtonUI>
        <ButtonUI
          onClick={handleManageObjectEvent}
          variant="contained"
          color="success"
          style={{ marginLeft: 15 }}
          disabled={loading || !folder}
        >
          Save
        </ButtonUI>
      </DialogActions>
    </KMKDialog>
  );
};

//Converting string into byte size for comparison
const getSize = (str) => {
  if (str.indexOf("GB") !== -1) return parseFloat(str) * 1000000000;
  else if (str.indexOf("MB") !== -1) return parseFloat(str) * 1000000;
  else if (str.indexOf("KB") !== -1) return parseFloat(str) * 1000;
  else if (str.indexOf("B") !== -1) return parseFloat(str);
  else return -1;
};

//Comparing afor sorting
const descendingComparator = (a, b, orderBy) => {
  if (orderBy === "size") {
    if (getSize(b[orderBy]) < getSize(a[orderBy])) return -1;
    if (getSize(b[orderBy]) > getSize(a[orderBy])) return 1;
    return 0;
  } else if (orderBy === "lastModified") {
    if (!b[orderBy] || !a[orderBy]) return b[orderBy] < a[orderBy] ? -1 : 1;

    if (new Date(b[orderBy]).getTime() < new Date(a[orderBy]).getTime()) return -1;
    if (new Date(b[orderBy]).getTime() > new Date(a[orderBy]).getTime()) return 1;
    return 0;
  } else {
    if (b[orderBy]?.toLowerCase() < a[orderBy]?.toLowerCase()) return -1;
    else if (b[orderBy]?.toLowerCase() > a[orderBy]?.toLowerCase()) return 1;
    return 0;
  }
};

//Comparing order
const getComparator = (order, orderBy) => {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
};

// This method is created for cross-browser compatibility, if you don't
// need to support IE11, you can use Array.prototype.sort() directly
const stableSort = (array, comparator) => {
  const stabilizedThis = array.map((el, index) => [el, index]);

  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    return order !== 0 ? order : a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
};

const FileBrowser = ({
  defaultPrefix = "",
  url,
  setOpenFileBrowser,
  title,
  setPath,
  stage,
  locationName = "",
  getData,
  createFolderClick,
  deleteClick,
  multipleDelete,
  multipleDownload,
  downloadClick,
  pref,
  bucketName,
  fileName,
  type,
  bucketNameArray,
  folderSelection = false,
  fileSelection = false,
  multipleFileSelection = false,
  hasUploadPermission = false,
  docuList,
}) => {
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [rows, setRows] = useState([]);
  const [selectedRows, setSelectedRows] = useState(() => (fileSelection && fileName ? fileName.split(",/") : []));
  const [selectedFolder, setSelectedFolder] = useState(() => (folderSelection && fileName ? fileName : ""));
  const [validLocation, setValidLocation] = useState(false);
  const [createFolder, setCreateFolder] = useState(false);
  const [oldFolder, setOldFolder] = useState("");
  const [folder, setFolder] = useState("");
  const [extension, setExtension] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteObject, setDeleteObject] = useState({});
  const [renameObject, setRenameObject] = useState(false);

  const [multipleDeleteState, setMultipleDeleteState] = useState({
    openConfirm: false,
    loading: false,
  });

  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("name");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(15);

  const dispatch = useDispatch();
  const upload = useRef(null);
  const { Workspace_id } = useParams();
  const processQueue = useSelector((state) => state.processQueue);

  //Setting prefix and filename
  const [prefix, setPrefix] = useState(pref);
  const prefixRef = useRef(prefix);

  const filteredRows = useMemo(
    () => rows.filter((fileObj) => fileObj.name.toLowerCase().includes(search.toLowerCase())),
    [rows, search]
  );

  type = type.toLowerCase();
  const tableHeadRef = useRef(null);

  const { classes } = useStyles();
  const { height: tableHeight } = useWindowResize(folderSelection || fileSelection ? 280 : 250);

  const disableOkBtn = useMemo(
    () =>
      Boolean(
        loading ||
          (folderSelection && selectedRows.length > 0) ||
          (fileSelection && (selectedRows.length === 0 || (!multipleFileSelection && selectedRows.length > 1)))
      ),
    [fileSelection, folderSelection, loading, multipleFileSelection, selectedRows.length]
  );

  const disableMultiActionBtn = useMemo(
    () => Boolean(loading || !selectedRows.length || selectedFolder),
    [loading, selectedFolder, selectedRows.length]
  );

  //Getting file and folder data
  const getAllData = useCallback(
    async (params = {}) => {
      setPage(0);
      setRows([]);
      if (folderSelection) setSelectedRows([]);

      setLoading(true);
      const response = await getData({
        bucketName: bucketName,
        prefix: prefixRef.current,
        Stage_id: stage,
        ...params,
      });

      //Checking if file path is correct or not
      if (response.success) {
        if (response.data.message) {
          setPrefix((prev) => {
            let tPrefix = "";
            if (type === "s3") {
              tPrefix = bucketNameArray?.join("/") || "";
              tPrefix = tPrefix && tPrefix[tPrefix.length - 1] !== "/" ? tPrefix + "/" : tPrefix;
            } else if (type === "azure") {
              tPrefix = prev.substring(0, prev.split("/", 2).join("/").length + 1);
            } else {
              tPrefix = "/";
            }
            return tPrefix;
          });
        }

        setRows(response.data.data);
        setValidLocation(true);
      } else {
        setOpenFileBrowser(false);
      }
      setLoading(false);
    },
    [bucketName, bucketNameArray, getData, multipleFileSelection, setOpenFileBrowser, stage, type]
  );

  //Checkbox click
  const handleCheckboxClick = useCallback((type, id, isChecked) => {
    if (type === "folder") return setSelectedFolder(isChecked ? "" : id);

    setSelectedRows((prev) => (isChecked ? prev.filter((_id) => _id !== id) : [...prev, id]));
  }, []);

  //Folder Click on row
  const handleFolderClick = useCallback(
    (name) => {
      setPrefix((prev) => `${prev}${name}/`);
      setSearch("");
      setSelectedFolder("");
      getAllData({
        prefix: `${prefix}${name}/`,
        search: "",
      });
    },
    [getAllData, prefix]
  );

  //Download click
  const handleDownloadClick = useCallback(
    async (e, id, name) => {
      e.stopPropagation();
      setLoading(true);

      await downloadClick({
        id,
        bucketName,
        fileName: name,
        Stage_id: stage,
      });

      setLoading(false);
    },
    [bucketName, downloadClick, stage]
  );

  //Download button click
  const handleMultipleDownload = useCallback(async () => {
    setLoading(true);

    await multipleDownload({
      bucketName,
      name: prefix,
      files: selectedRows,
      Stage_id: stage,
    });

    setLoading(false);
  }, [bucketName, multipleDownload, prefix, selectedRows, stage]);

  //Delete button click for deleting multiple files
  const handleMultipleDelete = useCallback(async () => {
    setPrevState(setMultipleDeleteState, { loading: true });
    const response = await multipleDelete({
      bucketName,
      name: prefix,
      files: selectedRows,
      Stage_id: stage,
      type: "file",
    });

    if (response.success) {
      dispatch(showToast({ message: response.message, type: "success" }));
      setPrevState(setMultipleDeleteState, { openConfirm: false, loading: false });
      await getAllData({ prefix: `${prefix}` });
    }

    setPrevState(setMultipleDeleteState, { openConfirm: false, loading: false });
    setSelectedRows([]);
  }, [bucketName, dispatch, getAllData, multipleDelete, prefix, selectedRows, stage]);

  const handleRefresh = async () => await getAllData({ prefix: `${prefix}` });

  //Creating delete object and opening delete confirmation
  const deleteConfirmation = useCallback((e, id, name, type) => {
    e.stopPropagation();
    if (type === "file")
      setDeleteObject({
        message: `Are you sure you want to delete the file "${name}"?`,
        title: "Delete file",
        id,
        type,
      });
    else
      setDeleteObject({
        message: `Are you sure you want to delete the folder "${name}" and its contents?`,
        title: "Delete folder",
        id,
        type,
      });

    setConfirmDelete(true);
  }, []);

  //Delete click
  const handleDeleteClick = useCallback(async () => {
    setLoading(true);
    const { id, type } = deleteObject;

    const response = await deleteClick({
      id,
      bucketName,
      Stage_id: stage,
      type,
    });

    if (response.success) {
      dispatch(showToast({ message: response.message, type: "success" }));
      setConfirmDelete(false);

      if (type === "folder" && selectedFolder === id) setSelectedFolder("");
      if (type === "file" && selectedRows.includes(id)) setSelectedRows((prev) => prev.filter((_id) => _id !== id));

      await getAllData({
        prefix: `${prefix}`,
      });
    } else {
      setConfirmDelete(false);
    }

    setLoading(false);
  }, [bucketName, deleteClick, deleteObject, dispatch, getAllData, prefix, selectedFolder, selectedRows, stage]);

  //Upload file
  const handleUploadFile = useCallback(
    async (e) => {
      const uploadedFiles = e.target.files;

      if (uploadedFiles?.length) {
        setLoading(true);
        let uploadQueueFiles = [];
        const uploadPromises = Array.from(uploadedFiles).map(async (file) => {
          const queueService = new ProcessQueueService();
          const { data, success } = await queueService.startUpload({
            Workspace_id: Workspace_id,
            Type: 1,
            Status: 1,
            FileName: file.name,
            FileSize: file.size,
            ConnectionName: locationName,
            Stage_Id: stage,
            ConnectionType: CONNECTION_TYPES[type.toLowerCase()],
            hasUploadPermission,
            s3bucketName: bucketName,
            prefix,
          });

          if (success && data?.isSpaceAvailable) {
            uploadQueueFiles.push({ file, ProcessQueue_id: data.ProcessQueue_id ?? null });
            dispatch(
              syncProcessQueue({
                processState: 1,
                Status: 1,
                file,
                ProcessQueue_id: data.ProcessQueue_id ?? null,
                bucketName,
                prefix,
                stage,
                connectionType: type,
              })
            );
          }
        });

        await Promise.all(uploadPromises);

        dispatch(
          showToast({
            message: "File upload started. Check the process queue or click the refresh button.",
            type: "success",
          })
        );
        setLoading(false);

        const startUploading = async () => {
          dispatch(updateProcessingStatus({ inProgress: true, fileUploadInProgress: true }));
          const uploader = new fileUploader();
          const response = await uploader.startUpload();
          // Update the processing queue after each upload
          if (response.ProcessQueue_id) {
            dispatch(syncProcessQueue({ processState: 2, ProcessQueue_id: response.ProcessQueue_id }));
            let remainingQueue = [
              ...(processQueue?.currentQueue.filter(
                (process) => process.ProcessQueue_id !== response.ProcessQueue_id
              ) ?? []),
            ];
            if (remainingQueue.length) startUploading();
          }
          dispatch(updateProcessingStatus({ inProgress: false, fileUploadInProgress: false }));
        };

        if (!processQueue.fileUploadInProgress && uploadQueueFiles.length) startUploading();
      }

      e.target.value = null;
    },
    [
      Workspace_id,
      bucketName,
      dispatch,
      hasUploadPermission,
      locationName,
      prefix,
      processQueue?.currentQueue,
      processQueue.fileUploadInProgress,
      stage,
      type,
    ]
  );

  //Close Create folder dislog
  const handleCloseEvent = useCallback(() => {
    setOldFolder("");
    setFolder("");
    setCreateFolder(false);
  }, []);

  //Create Folder
  const handleManageObjectEvent = useCallback(
    async (objType = "folder") => {
      //Checking if folder with name already exists
      if (
        rows.some(
          (obj) =>
            (!oldFolder && obj.name.toLowerCase() === folder.toLowerCase()) ||
            (oldFolder &&
              oldFolder.toLowerCase() !== folder.toLowerCase() &&
              obj.name.toLowerCase() === folder.toLowerCase())
        )
      )
        dispatch(showToast({ message: "There is already a folder with same name in the location!", type: "error" }));
      else {
        setLoading(true);
        const fileType = objType === "folder" ? "" : "." + extension;
        const response = await createFolderClick({
          bucketName,
          name: prefix + folder + fileType,
          Stage_id: stage,
          oldKey: oldFolder ? prefix + oldFolder : "",
          objType: objType || "folder",
        });

        if (response.success) {
          handleCloseEvent();
          dispatch(showToast({ message: response.message, type: "success" }));

          // updating values for selections
          const _prefix = prefix.replace(defaultPrefix, "");
          const oldPath = _prefix + oldFolder;

          if (fileSelection && selectedRows.includes(oldPath))
            setSelectedRows((prev) => prev.map((_file) => (_file === oldPath ? _prefix + folder + fileType : _file)));
          if (folderSelection && selectedFolder === oldPath) setSelectedFolder(_prefix + folder + fileType);

          await getAllData({
            prefix: `${prefix}`,
          });
        }

        setLoading(false);
      }
    },
    [
      bucketName,
      createFolderClick,
      defaultPrefix,
      dispatch,
      extension,
      fileSelection,
      folder,
      folderSelection,
      getAllData,
      handleCloseEvent,
      oldFolder,
      prefix,
      rows,
      selectedFolder,
      selectedRows,
      stage,
    ]
  );

  //Clear values for manage Object
  const handleRenameEvent = useCallback(
    (type, oldFolder) => {
      let name = oldFolder;
      if (type === "file") {
        name = oldFolder.substr(0, oldFolder.lastIndexOf("."));
      }

      if (folder.trim() && folder !== name) {
        handleManageObjectEvent(type);
      } else {
        setOldFolder("");
        setFolder("");
        setExtension("");
      }

      setRenameObject(false);
    },
    [folder, handleManageObjectEvent]
  );

  //Validate Folder Name
  const handleFolderName = useCallback((str) => {
    if (regexFileName(str)) setFolder(str);
  }, []);

  //Close popup
  const handleCancel = useCallback(() => {
    setOpenFileBrowser(false);
  }, [setOpenFileBrowser]);

  //Clear search
  const handleClearSearch = useCallback(() => {
    setSearch("");
  }, []);

  useEffect(() => {
    getAllData();

    return () => {
      setValidLocation(false);
    };
  }, []);

  useEffect(() => setPage(0), [search]);

  //breadcrumbs for folders
  const BucketTree = useCallback(() => {
    let tPrefix = "";

    if (type === "s3") {
      tPrefix = prefix.replace(url.replace("s3://" + bucketName + "/", ""), "");

      //Removing first and last /
      if (tPrefix) {
        if (tPrefix.startsWith("/")) tPrefix = tPrefix.substring(1);
        if (tPrefix.endsWith("/")) tPrefix = tPrefix.slice(0, tPrefix.length - 1);
      }
    } else {
      tPrefix = prefix.replace(url, "");
    }

    return (
      <Box className={classes.BucketContainer}>
        {tPrefix &&
        (type === "s3" || ((type === "sftp" || type === "azure" || type === "sharepoint") && tPrefix !== "/")) ? (
          <ListItemButton
            disableRipple
            className={classes.listItem}
            children={url}
            underline="hover"
            onClick={() => {
              let tPrefix = "";
              if (type === "s3") {
                tPrefix = bucketNameArray?.join("/") || "";
                tPrefix = tPrefix && tPrefix[tPrefix.length - 1] !== "/" ? tPrefix + "/" : tPrefix;
              } else {
                tPrefix = "/";
              }

              setPrefix(tPrefix);
              setSearch("");
              getAllData({
                prefix: tPrefix,
                search: "",
              });
            }}
          />
        ) : (
          url
        )}
        {type === "s3" && tPrefix && " > "}
        {tPrefix &&
          tPrefix
            .split("/")
            .map((text, key, arr) => {
              return (type === "s3" && key === arr.length - 1) ||
                ((type === "sftp" || type === "azure" || type === "sharepoint") && key === arr.length - 2) ? (
                text
              ) : (
                <ListItemButton
                  key={key}
                  disableRipple
                  className={classes.listItem}
                  underline="hover"
                  onClick={() => {
                    arr.length = key + 1;
                    const res = arr.join("/") + "/";

                    setPrefix(prefix.substring(0, prefix.indexOf(res) + res.length));
                    setSearch("");
                    getAllData({
                      prefix: prefix.substring(0, prefix.indexOf(res) + res.length),
                      search: "",
                    });
                  }}
                >
                  <ListItemText primary={text} />
                </ListItemButton>
              );
            })
            .reduce((prev, curr) => [prev, "/", curr])}
        <CopyButton
          value={
            (type === "s3" || type === "sharepoint"
              ? `${url}${!url.endsWith("/") && !tPrefix.startsWith("/") ? "/" : ""}`
              : "") + tPrefix
          }
        />
      </Box>
    );
  }, [bucketName, bucketNameArray, classes.BucketContainer, classes.listItem, getAllData, prefix, type, url]);

  // Selecting/unselecting from current location only
  const handleSelectAllFiles = useCallback(
    (e) => {
      if (e.target.checked)
        setSelectedRows((prev) => [
          ...new Set([...prev, ...filteredRows.filter(({ type }) => type === "file").map(({ id }) => id)]),
        ]);
      else {
        const itemsToBeRemoved = filteredRows.filter(({ type }) => type === "file").map(({ id }) => id);
        setSelectedRows((prev) => prev.filter((id) => !itemsToBeRemoved.includes(id)));
      }
    },
    [filteredRows]
  );

  const handleRemoveItem = useCallback((removedItem) => {
    setSelectedRows((prev) => prev.filter((item) => item !== removedItem));
  }, []);

  //Sort cloumn click
  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  //next page click
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  //rows per page dropdown click
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const $finalData = useMemo(
    () =>
      stableSort(filteredRows, getComparator(order, orderBy)).slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
      ),
    [filteredRows, order, orderBy, page, rowsPerPage]
  );

  const renderObjectList = () => {
    return $finalData.map(({ id, type: objectType, fileExtension, fileName, name, lastModified, size }) => {
      const checked = objectType === "folder" ? selectedFolder === id : selectedRows.includes(id);

      return (
        <TableRow
          key={id}
          sx={{
            "&:last-child td, &:last-child th": { border: 0 },
          }}
          className={`${classes.selectableRow} ${checked ? classes.selectedRow : ""}`}
          onClick={(e) => {
            if (objectType === "folder" && !folderSelection) return;
            e.stopPropagation();

            handleCheckboxClick(objectType, id, checked);
          }}
          onDoubleClick={() => objectType === "folder" && !renameObject && handleFolderClick(name)}
        >
          <TableCell component="th" scope="row" sx={{ padding: "0px 5px" }}>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Checkbox
                sx={{ justifyContent: "center", alignItems: "center", ml: 1.25, mr: 1.5 }}
                margin="auto"
                color="primary"
                checked={checked}
                disabled={objectType === "folder" && !folderSelection}
              />

              {objectType === "file" ? (
                <>
                  <InsertDriveFileOutlinedIcon sx={{ mr: 1.5, color: "#4d433c" }} />

                  <Box className={classes.FirstTableCell}>
                    {renameObject && name === oldFolder ? (
                      <TextField
                        margin="normal"
                        value={folder}
                        required
                        autoFocus
                        onChange={(e) => handleFolderName(e.target.value)}
                        inputProps={{ maxLength: 255 }}
                        onClick={(e) => e.stopPropagation()}
                        onBlur={() => {
                          handleRenameEvent(objectType, oldFolder);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") handleRenameEvent(objectType, oldFolder);
                        }}
                        variant="standard"
                        sx={{ mr: "20px" }}
                      />
                    ) : (
                      name
                    )}
                  </Box>
                </>
              ) : (
                <>
                  <FolderOutlinedIcon sx={{ mr: 1.5, color: "#c99944" }} />
                  <Box
                    className={`${classes.bucketNameField} ${classes.FirstTableCell}`}
                    style={{ textTransform: "none", paddingLeft: 0, marginLeft: 0 }}
                  >
                    {renameObject && name === oldFolder ? (
                      <TextField
                        margin="normal"
                        value={folder}
                        required
                        autoFocus
                        onChange={(e) => handleFolderName(e.target.value)}
                        inputProps={{ maxLength: 255 }}
                        onBlur={() => {
                          handleRenameEvent(objectType, oldFolder);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") handleRenameEvent(objectType, oldFolder);
                        }}
                        variant="standard"
                        sx={{ mr: "20px" }}
                      />
                    ) : (
                      name
                    )}
                  </Box>
                </>
              )}
            </Box>
          </TableCell>
          <TableCell align="center" sx={{ padding: "0px 5px" }}>
            {fileExtension.includes("Folder")
              ? fileExtension
              : `${(fileExtension.toUpperCase() + " File").slice(0, 15)}${
                  (fileExtension.toUpperCase() + " File").length > 15 ? "..." : ""
                }`}
          </TableCell>
          <TableCell align="center" sx={{ padding: "0px 5px" }}>
            {size}
          </TableCell>
          <TableCell align="center" sx={{ maxWidth: "150px", padding: "0px 5px" }}>
            {lastModified ? convertTimezone(lastModified) : ""}
          </TableCell>
          <TableCell align="right" sx={{ minWidth: "90px", padding: "0px 5px" }}>
            {objectType === "file" && (
              <Tooltip title="Download">
                <span>
                  <IconButton color="info" sx={{ maxWidth: "33%" }} onClick={(e) => handleDownloadClick(e, id, name)}>
                    <FileDownloadIcon />
                  </IconButton>
                </span>
              </Tooltip>
            )}
            {type === "s3" && (
              <Tooltip title={objectType === "file" ? "Rename File" : "Rename Folder"}>
                <span>
                  <IconButton
                    sx={{ maxWidth: "33%" }}
                    color="primary"
                    onClick={(e) => {
                      e.stopPropagation();
                      setOldFolder(name);
                      setFolder(fileName);
                      setExtension(fileExtension);
                      setRenameObject(true);
                    }}
                  >
                    <EditOutlinedIcon />
                  </IconButton>
                </span>
              </Tooltip>
            )}
            <Tooltip title="Delete">
              <span>
                <IconButton
                  color="error"
                  sx={{ maxWidth: "33%" }}
                  onClick={(e) => deleteConfirmation(e, id, name, objectType)}
                >
                  <DeleteOutlineIcon />
                </IconButton>
              </span>
            </Tooltip>
          </TableCell>
        </TableRow>
      );
    });
  };

  const isAnyFileUnselected = useMemo(
    () => selectedRows.length > 0 && $finalData.some(({ id, type }) => type === "file" && !selectedRows.includes(id)),
    [$finalData, selectedRows]
  );

  return (
    <>
      <Backdrop
        sx={{ backgroundColor: "transparent", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading && !validLocation}
      >
        <CircularProgress color="primary" />
      </Backdrop>
      <KMKDialog
        TransitionComponent={Transition}
        className={classes.dialogPaper}
        maxWidth="md"
        open={validLocation}
        sx={{
          "& .MuiDialog-paperFullScreen": {
            maxHeight: "97%",
            width: "97%",
          },
        }}
        fullScreen
        onClose={(e) => {
          if (e.type === "click") return;
          handleCancel();
        }}
      >
        <AppBar sx={{ position: "relative" }}>
          <Toolbar className={classes.Toolbar} variant="dense">
            {type === "s3" ? (
              <img src="/images/aws-s3-logo.png" alt="S3" className={classes.LogoImg} />
            ) : type === "azure" ? (
              <img src="/images/azure-logo.png" alt="Azure" className={classes.LogoImg} />
            ) : type === "sftp" ? (
              <img src="/images/sftp-logo.png" alt="SFTP" className={classes.LogoImg} />
            ) : type === "sharepoint" ? (
              <img
                src="/images/sharepoint.png"
                alt="SharePoint"
                className={classes.LogoImg}
                style={{ width: "27px" }}
              />
            ) : (
              <></>
            )}
            <>{title}</>
          </Toolbar>
          <IconButton
            aria-label="close"
            onClick={handleCancel}
            sx={{
              position: "absolute",
              right: 8,
              top: 4,
              color: "black",
            }}
          >
            <CloseIcon />
          </IconButton>
        </AppBar>
        <Box sx={{ width: "100%" }} className={classes.container}>
          <Backdrop sx={{ backgroundColor: "transparent", zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
            <CircularProgress color="primary" />
          </Backdrop>
          <Box sx={{ mb: 2, height: tableHeight + 150 }} className={classes.bodyContainer}>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Box className={classes.searchContainer}>
                <TextField
                  margin="normal"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search"
                  variant="standard"
                  InputProps={{
                    startAdornment: <SearchIcon color="primary" />,
                    ...(search && {
                      endAdornment: (
                        <IconButton onClick={handleClearSearch} size="small">
                          <ClearIcon color="primary" />
                        </IconButton>
                      ),
                    }),
                  }}
                  sx={{ minWidth: "100%" }}
                />
              </Box>
              {!(type === "azure" && prefix === "/") && (
                <>
                  <input
                    type="file"
                    ref={upload}
                    style={{ display: "none" }}
                    onChange={handleUploadFile}
                    multiple="multiple"
                  />
                  <ButtonUI
                    className={classes.headerIcons}
                    variant="contained"
                    style={{ width: "auto", background: "#283264" }}
                    onClick={(e) => upload.current.click()}
                  >
                    Upload
                  </ButtonUI>
                </>
              )}
              <ButtonUI
                className={classes.headerIcons}
                variant="contained"
                style={{ width: "auto", background: "#3E7E36" }}
                onClick={(e) => setCreateFolder(true)}
              >
                Create Folder
              </ButtonUI>
              <ButtonUI
                color="secondary"
                className={classes.headerIcons}
                variant="contained"
                style={{ width: "auto" }}
                disabled={disableMultiActionBtn}
                onClick={handleMultipleDownload}
              >
                Download
              </ButtonUI>
              <ButtonUI
                color="error"
                className={classes.headerIcons}
                variant="contained"
                style={{ width: "auto" }}
                disabled={disableMultiActionBtn}
                onClick={() => setPrevState(setMultipleDeleteState, { openConfirm: true })}
              >
                Delete
              </ButtonUI>
              <Typography sx={{ ml: "0px" }}>
                <SelectedFilesDialog selectedRows={selectedRows} removeItem={handleRemoveItem}>
                  <Chip
                    component="span"
                    color="info"
                    sx={{ cursor: "pointer", mr: 1 }}
                    label={selectedRows.length + +!!selectedFolder}
                    disabled={selectedRows.length + +!!selectedFolder === 0}
                  />
                </SelectedFilesDialog>
                Item(s) selected
              </Typography>
              <Tooltip title="Refresh">
                <IconButton onClick={(e) => handleRefresh()} sx={{ ml: "auto" }}>
                  <RefreshOutlinedIcon color="secondary" sx={{ fontSize: "26px" }} />
                </IconButton>
              </Tooltip>
            </Box>
            {BucketTree()}
            <TableContainer component={Paper} sx={{ height: tableHeight, overflow: "auto" }}>
              <Table stickyHeader sx={{ minWidth: 650 }} size="small">
                <EnhancedTableHead
                  className={classes.TableHead}
                  order={order}
                  orderBy={orderBy}
                  onRequestSort={handleRequestSort}
                  tableHeadRef={tableHeadRef}
                  isAnyFileUnselected={isAnyFileUnselected}
                  selectedFileCount={selectedRows.length}
                  handleSelectAllFiles={handleSelectAllFiles}
                  folderSelection={folderSelection}
                />
                {$finalData?.length > 0 ? (
                  <TableBody>{renderObjectList()}</TableBody>
                ) : (
                  <TableBody sx={{ height: tableHeight - (tableHeadRef?.current?.clientHeight || 37) }}>
                    <TableRow tabIndex={-1}>
                      <TableCell align={"center"} colSpan={5}>
                        No records found
                      </TableCell>
                    </TableRow>
                  </TableBody>
                )}
              </Table>
            </TableContainer>
            <TablePagination
              className={classes.TablePagination}
              rowsPerPageOptions={[15, 25, 50, 100]}
              component="div"
              count={filteredRows.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Box>
          {(folderSelection || fileSelection) && (
            <DialogActions sx={{ px: 3, py: 0 }}>
              <ButtonUI variant="contained" color="secondary" onClick={handleCancel}>
                Cancel
              </ButtonUI>
              <ButtonUI
                onClick={() => {
                  if (fileSelection) setPath("/" + selectedRows.join(",/"));
                  else {
                    let $selectedFolder = selectedFolder || `${prefix}`.replace(defaultPrefix, "");

                    if ($selectedFolder) {
                      if (!$selectedFolder.startsWith("/")) $selectedFolder = "/" + $selectedFolder;
                      if (!$selectedFolder.endsWith("/")) $selectedFolder = $selectedFolder + "/";
                    }

                    setPath($selectedFolder || "/");
                  }

                  handleCancel();
                }}
                variant="contained"
                color="success"
                style={{ marginLeft: 15 }}
                disabled={disableOkBtn}
              >
                Ok
              </ButtonUI>
            </DialogActions>
          )}
        </Box>
        {createFolder && (
          <PopupCreateFolder
            folderPath={
              type === "s3"
                ? "s3://" + bucketName + (bucketName[bucketName.length - 1] === "/" ? "" : "/") + prefix
                : type === "sharepoint"
                ? docuList + prefix
                : prefix
            }
            handleClose={handleCloseEvent}
            handleManageObjectEvent={() => handleManageObjectEvent()}
            setFolder={setFolder}
            folder={folder}
            handleFolderName={handleFolderName}
            loading={loading}
          />
        )}

        {multipleDeleteState.openConfirm && (
          <ConfirmPopup
            configData={{
              popupTitle: "Delete File(s)",
              popupMessage: "Are you sure you want to delete the selcted file(s)?",
              handleCancel: () => setPrevState(setMultipleDeleteState, { openConfirm: false }),
              handleClick: handleMultipleDelete,
              loadingForConfirm: multipleDeleteState.loading,
            }}
          />
        )}

        {confirmDelete && (
          <ConfirmPopup
            configData={{
              popupTitle: deleteObject.title,
              popupMessage: deleteObject.message,
              handleCancel: () => setConfirmDelete(false),
              handleClick: handleDeleteClick,
              loadingForConfirm: loading,
            }}
          />
        )}
      </KMKDialog>
    </>
  );
};

export default FileBrowser;
